import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { X } from "lucide-react";
import { useRouter } from "next/router";

const NvidiaNotification = () => {
  const router = useRouter();
  const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [showAutoTooltip, setShowAutoTooltip] = useState(false);
  const componentRef = useRef(null);

  useEffect(() => {
    // Check if user has already dismissed the tooltip notification
    const hasSeenNotification = sessionStorage.getItem(
      "nvidia-notification-dismissed"
    );
    if (hasSeenNotification) {
      setIsTooltipDismissed(true);
    } else {
      // Show tooltip automatically on first page load after a delay
      const timer = setTimeout(() => {
        setShowAutoTooltip(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle click outside to close tooltips
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target)
      ) {
        if (showAutoTooltip) {
          setShowAutoTooltip(false);
        }
        if (isClicked) {
          setIsClicked(false);
        }
        if (isHovered) {
          setIsHovered(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAutoTooltip, isClicked, isHovered]);

  const handleClose = (e) => {
    e.stopPropagation();
    setIsTooltipDismissed(true);
    setShowAutoTooltip(false);
    setIsHovered(false);
    setIsClicked(false);
    sessionStorage.setItem("nvidia-notification-dismissed", "true");
  };

  const handleClick = (e) => {
    e.stopPropagation();
    // Mark notification as dismissed when user reads the announcement
    setIsTooltipDismissed(true);
    setShowAutoTooltip(false);
    setIsHovered(false);
    setIsClicked(false);
    sessionStorage.setItem("nvidia-notification-dismissed", "true");

    // Track click event if needed
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "nvidia_notification",
        event_label: "navbar_announcement",
      });
    }

    router.push("/announcement/nvidia-inception-program");
  };

  const handleLogoClick = () => {
    // Toggle click state and clear other states
    setIsClicked(!isClicked);
    setIsHovered(false); // Clear hover state when manually clicking
    setShowAutoTooltip(false); // Hide auto tooltip when manually interacting
  };

  // Show tooltip if:
  // 1. Auto-showing on first load (and not dismissed)
  // 2. User hovered (stays open until manually closed)
  // 3. User clicked the logo (works even if dismissed)
  const showTooltip =
    (!isTooltipDismissed && showAutoTooltip) || isHovered || isClicked;
  return (
    <div className="relative" ref={componentRef}>
      {/* NVIDIA Logo with notification dot */}
      <div
        className="relative cursor-pointer"
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          // Don't automatically close on hover out - let user manually close
        }}
        onClick={handleLogoClick}
      >
        <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
          <Image
            src={require(`assets/announcements/nvidia-logo.png`)}
            alt="NVIDIA Logo"
            width={28}
            height={28}
            className="object-contain"
          />
          {/* Notification dot - only show if tooltip hasn't been dismissed */}
          {!isTooltipDismissed && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse border-2 border-white"></div>
          )}
        </div>

        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute top-full mt-2 z-50 right-0 lg:right-[-50px]">
            <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-72 max-w-[calc(100vw-1rem)] relative animate-fadeIn">
              {/* Arrow pointing up - responsive positioning */}
              <div className="absolute -top-2 right-6 lg:right-[74px] w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-white"></div>
              <div className="absolute -top-3 right-6 lg:right-[74px] w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-200"></div>

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100"
              >
                <X size={16} />
              </button>

              {/* Content */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M9 12l2 2 4-4" />
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="white"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1 pr-4">
                  <h4 className="font-bold text-gray-900 text-sm mb-1">
                    🎉 We joined NVIDIA Inception!
                  </h4>
                  <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                    BoloSign has been accepted into the NVIDIA Inception
                    Program, bringing AI-powered contract intelligence to the
                    next level.
                  </p>
                  <button
                    onClick={handleClick}
                    className="inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-md"
                  >
                    Read announcement
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      className="ml-1"
                    >
                      <path d="M7 17l9.2-9.2M17 17V7H7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NvidiaNotification;
